import { createAsyncThunk } from "@reduxjs/toolkit";

import { fetchWithAuth } from "~/common/fetch";
import { formSubmitNotification } from "~/components/notifications/form_submit_pop";
import { saveDraftPatientNotification } from "~/components/notifications/save_patient_draft";
import {
  API_POST_CORRECTION,
  API_POST_NEW_MIDTERM_CORRECTION,
  API_PUT_CORRECTION,
  API_PUT_PATIENT,
  API_SUBMIT_CORRECTION,
} from "~/config";
import type { TUserActionNotification } from "~/reducers/dashboard";
import type { AppDispatch } from "~/store";
import type { TCorrection, TPatient } from "~/types/patient";
import type { TPrescription } from "~/types/prescription";

import { addUserActionNotification } from "./user_notification";

const NOTIFICATION_MSG_OK: TUserActionNotification = {
  message: "notify.form.submitted.ok",
  level: "success",
  position: "tc",
  autoDismiss: 10,
  children: formSubmitNotification(),
};

const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
  message: "notify.correction.create.error",
  level: "error",
  position: "tl",
  autoDismiss: 4,
};

export function sendPatientCorrection(
  patient_id: TPatient["patient_id"],
  media_info: Record<string, unknown>,
  s3_media: Record<string, unknown>,
  links: string[] | null = null,
  stage: number,
  prescription: TPrescription,
  with_ct: boolean | null = null, // must be null if absent, backend validation expects it
  test_plastic: boolean | null = null,
) {
  const data = { stage, prescription, with_ct, test_plastic };

  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(API_POST_NEW_MIDTERM_CORRECTION(patient_id), {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(
          "Error occurred trying to order correction: API_POST_NEW_MIDTERM_CORRECTION",
        );
      }
      await response.json();

      const response2 = await fetchWithAuth(API_PUT_PATIENT(patient_id), {
        method: "PUT",
        body: JSON.stringify({ media_info, s3_media, links }),
      });
      if (!response2.ok) {
        throw new Error("Error occurred trying to order correction: API_PUT_PATIENT");
      }
      const json = await response2.json();

      // NOTE: The timeout is needed because we want to render the notification after we
      // have been redirected to the patients page. Otherwise incorrect styles are applied to the popup
      // and it appears at the top of the screen, half invisible.
      window.setTimeout(() => dispatch(addUserActionNotification(NOTIFICATION_MSG_OK)));
      dispatch({ type: "ADD_PATIENT_SUCCESSFULL", json });
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  };
}

type CreateCorrectionArgs = {
  patient_id: TPatient["patient_id"];
  stage: number;
  prescription: TPrescription;
  with_ct?: boolean | null;
  test_plastic?: boolean | null;
  media_info: unknown;
  s3_media: unknown;
  links: string[];
};

export const createCorrection = createAsyncThunk(
  "correction/create",
  async function (
    {
      patient_id,
      stage,
      prescription,
      with_ct = null,
      test_plastic = null,
      media_info,
      s3_media,
      links,
    }: CreateCorrectionArgs,
    { dispatch },
  ) {
    try {
      const response = await fetchWithAuth(API_POST_CORRECTION(patient_id), {
        method: "POST",
        body: JSON.stringify({ stage, prescription, with_ct, test_plastic }),
      });
      if (!response.ok) {
        throw new Error("Error occurred trying to create correction: API_POST_CORRECTION");
      }

      const response2 = await fetchWithAuth(API_PUT_PATIENT(patient_id), {
        method: "PUT",
        body: JSON.stringify({ media_info, s3_media, links }),
      });
      if (!response2.ok) {
        throw new Error("Error occurred trying to create correction: API_PUT_PATIENT");
      }

      // NOTE: The timeout is needed because we want to render the notification after we
      // have been redirected to the patients page. Otherwise incorrect styles are applied to the popup
      // and it appears at the top of the screen, half invisible.
      window.setTimeout(() => {
        dispatch(
          addUserActionNotification({
            message: "notify.patient.create.draft.ok",
            level: "success",
            position: "tc",
            autoDismiss: 0,
            children: saveDraftPatientNotification(),
          }),
        );
      });
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  },
);

export const updateCorrection = createAsyncThunk(
  "correction/update",
  async function (
    {
      correction_id,
      patient_id,
      stage,
      prescription,
      with_ct = null,
      test_plastic = null,
      media_info,
      s3_media,
      links,
    }: CreateCorrectionArgs & { correction_id: TCorrection["id"] },
    { dispatch },
  ) {
    try {
      const response = await fetchWithAuth(API_PUT_CORRECTION(patient_id, correction_id), {
        method: "PUT",
        body: JSON.stringify({ stage, prescription, with_ct, test_plastic }),
      });
      if (!response.ok) {
        throw new Error("Error occurred trying to update correction: API_PUT_CORRECTION");
      }

      const response2 = await fetchWithAuth(API_PUT_PATIENT(patient_id), {
        method: "PUT",
        body: JSON.stringify({ media_info, s3_media, links }),
      });
      if (!response2.ok) {
        throw new Error("Error occurred trying to create correction: API_PUT_PATIENT");
      }
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  },
);

export const submitCorrection = createAsyncThunk(
  "correction/update",
  async function (
    {
      patient_id,
      correction_id,
    }: { patient_id: TPatient["patient_id"]; correction_id: TCorrection["id"] },
    { dispatch },
  ) {
    const response = await fetchWithAuth(API_SUBMIT_CORRECTION(patient_id, correction_id), {
      method: "POST",
    });

    if (!response.ok) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw new Error("Error occurred trying to submit correction: API_SUBMIT_CORRECTION");
    }

    // NOTE: The timeout is needed because we want to render the notification after we
    // have been redirected to the patients page. Otherwise incorrect styles are applied to the popup
    // and it appears at the top of the screen, half invisible.
    window.setTimeout(() => dispatch(addUserActionNotification(NOTIFICATION_MSG_OK)));
  },
);
